// ── Mock types for ExpeditionDetailPage ──────────────────────────────────────
// These local types mirror future backend fields that do not yet exist in the
// GraphQL schema. Each one is marked with a TODO so they are easy to find when
// the corresponding backend field is ready.
//
// TODO(backend/tracking):   replace LiveTracking with VesselPing subscription type
// TODO(backend/waypoints):  add `type` field to Waypoint (DEPARTURE/STOPOVER/ANCHORAGE/ARRIVAL)
// TODO(backend/expedition): add `description`, `organiser`, `berthPrice`, `budget`,
//                           `currency`, `dayNumber`, `totalDays`, `coveredNm`,
//                           `departsLabel`, `arrivesLabel`, `duration` to Expedition
// TODO(backend/crew):       add `initials`, `paid`, `joinType` display helpers
// TODO(backend/documents):  add document storage (post-MVP)
// TODO(backend/activity):   add activity log (post-MVP)

import type { CrewRole, ExpeditionKind, ExpeditionStatus as GeneratedExpeditionStatus } from "~/graphql/generated";

// ── Status (local lowercase variant — TODO: migrate to generated ExpeditionStatus SCREAMING_SNAKE_CASE) ──

export type ExpeditionStatus = "planning" | "open" | "fully_booked" | "active" | "completed" | "cancelled";

export type MembershipStatus =
  | "CONFIRMED"
  | "PENDING_ACCEPTANCE"
  | "PENDING_APPROVAL"
  | "PENDING_PAYMENT"
  | "CANCELLED";

export type JoinType = "invited" | "paid_berth" | "applied";

// ── Shape types ───────────────────────────────────────────────────────────────

export interface WaypointEntry {
  name: string;
  lat: string;
  lng: string;
  // TODO(backend/waypoints): add `type` to Waypoint in schema
  type: "DEPARTURE" | "STOPOVER" | "ANCHORAGE" | "ARRIVAL";
  etaLabel?: string;
  actualLabel?: string;
  notes?: string;
  legNm?: number;
}

export interface CrewMember {
  id: string;
  initials: string;
  name: string;
  email: string;
  role: CrewRole;
  status: MembershipStatus;
  joinType: JoinType;
  milesEarned?: number;
  // TODO(backend/crew): add `paid` / `settledAt` to CrewMembership
  paid: boolean;
  isOwner?: boolean;
  vesselId?: string;
}

export interface VesselEntry {
  id: string;
  flag: string;
  name: string;
  type: string;
  berths: number;
}

export interface DocumentEntry {
  id: string;
  name: string;
  type: string;
  uploadedLabel: string;
}

export interface ExpenseEntry {
  id: string;
  description: string;
  amount: number;
  displayAmount: string;
  category: string;
  paidBy: string;
  settled: boolean;
}

export interface ActivityEntry {
  id: string;
  title: string;
  detail: string;
  timeLabel: string;
  color: "success" | "warning" | "primary" | "neutral";
}

// TODO(backend/tracking): replace with VesselPing subscription shape
export interface LiveTracking {
  position: string;
  speed: string;
  heading: string;
  wind: string;
  lastPing: string;
  vessel: string;
}

// ── Config ────────────────────────────────────────────────────────────────────

export const STATUS_GRADIENT: Record<ExpeditionStatus, string> = {
  planning: "from-abyss-400 to-abyss-600",
  open: "from-shallow-400 to-shallow-600",
  fully_booked: "from-signal-400 to-signal-600",
  active: "from-abyss-500 to-abyss-300",
  completed: "from-violet-500 to-violet-300",
  cancelled: "from-coral-400 to-coral-600",
};

// Gradient keyed by the generated (SCREAMING_SNAKE_CASE) ExpeditionStatus enum.
// Used by ExpeditionDetailPage once the page is wired to real data.
export const STATUS_GRADIENT_REAL: Record<GeneratedExpeditionStatus, string> = {
  PLANNING: "from-abyss-400 to-abyss-600",
  OPEN: "from-shallow-400 to-shallow-600",
  FULLY_BOOKED: "from-signal-400 to-signal-600",
  ACTIVE: "from-abyss-500 to-abyss-300",
  COMPLETED: "from-violet-500 to-violet-300",
  CANCELLED: "from-coral-400 to-coral-600",
};

export const VESSEL_GRADIENTS = [
  "from-abyss-500 to-abyss-300",
  "from-shallow-400 to-shallow-600",
  "from-violet-400 to-violet-600",
  "from-signal-400 to-signal-500",
];

export const WAYPOINT_TYPE_BADGE: Record<WaypointEntry["type"], { label: string; className: string }> = {
  DEPARTURE: { label: "Departure", className: "bg-abyss-50 text-abyss-600 dark:bg-abyss-900" },
  STOPOVER: { label: "Stopover", className: "bg-muted text-muted-foreground" },
  ANCHORAGE: { label: "Anchorage", className: "bg-muted text-muted-foreground" },
  ARRIVAL: { label: "Arrival", className: "bg-success-muted text-success-dark" },
};

export const EXPENSE_CATEGORIES: { key: string; label: string; color: string }[] = [
  { key: "Marina fees", label: "Marina", color: "bg-abyss-500" },
  { key: "Fuel", label: "Fuel", color: "bg-shallow-500" },
  { key: "Provisioning", label: "Provisions", color: "bg-signal-500" },
  { key: "Entry fees", label: "Entry fees", color: "bg-violet-500" },
  { key: "Safety", label: "Safety", color: "bg-coral-500" },
];

// ── Mock data object ──────────────────────────────────────────────────────────
// TODO(backend): Remove this entire object once ExpeditionBySlug returns all
// the fields used here. Fields already available in the schema are annotated.

export const EXPEDITION = {
  // TODO(backend): real — id, slug, name (mapped to title below), kind, status
  id: "1",
  slug: "arc-2026",
  title: "ARC 2026",
  kind: "RALLY" as ExpeditionKind,
  status: "active" as ExpeditionStatus,

  // TODO(backend/expedition): add description to Expedition type
  description:
    "The Atlantic Rally for Cruisers (ARC) is the world's largest transatlantic rally. Departing from Las Palmas de Gran Canaria, Spain, and arriving in Rodney Bay, St Lucia — 2,700 nautical miles across the Atlantic.",

  // TODO(backend/expedition): format departureDate/returnDate from real fields
  departsLabel: "22 Nov 2026",
  arrivesLabel: "13 Dec 2026",
  duration: "21 days",

  // TODO(backend/tracking): dayNumber + totalDays derive from departureDate + today
  dayNumber: 8,
  totalDays: 21,

  // TODO(backend): totalDistanceNm comes from route.totalDistanceNm
  distanceNm: 2700,

  // TODO(backend/tracking): coveredNm comes from VesselPing subscription
  coveredNm: 1020,

  // TODO(backend): berthsFilled = confirmed crew count; berthsTotal = maxCrew
  berthsFilled: 6,
  berthsTotal: 8,

  // TODO(backend/expedition): add budget + currency to Expedition type
  budget: 6500,
  currency: "€",

  // TODO(backend): vessel comes from vessels[0].vessel
  vessel: { flag: "🇵🇹", name: "SY Aventura II", type: "Catamaran", lengthM: 14.8, year: 2019 },

  // TODO(backend): vessels[] comes from ExpeditionBySlug vessels[]
  vessels: [
    { id: "v1", flag: "🇵🇹", name: "SY Aventura II", type: "Catamaran", berths: 6 },
    { id: "v2", flag: "🇳🇴", name: "SY Nordic Spirit", type: "Sloop", berths: 4 },
  ] as VesselEntry[],

  // TODO(backend/expedition): add organiser, homePort, berthPrice to schema
  organiser: "Ocean Crew Club",
  homePort: "Las Palmas de Gran Canaria",
  berthPrice: "€1,200 / person",

  // TODO(backend): waypoints come from route.waypoints — missing `type` field
  waypoints: [
    {
      name: "Las Palmas de Gran Canaria",
      lat: "28°06′N",
      lng: "15°24′W",
      type: "DEPARTURE",
      etaLabel: "22 Nov",
      actualLabel: "22 Nov",
      notes: "ARC rally start",
      legNm: 0,
    },
    {
      name: "Cape Verde (optional)",
      lat: "16°54′N",
      lng: "24°59′W",
      type: "ANCHORAGE",
      etaLabel: "26 Nov",
      notes: "Voluntary stop",
      legNm: 850,
    },
    {
      name: "Mid-Atlantic waypoint",
      lat: "20°30′N",
      lng: "40°00′W",
      type: "STOPOVER",
      etaLabel: "2 Dec",
      actualLabel: "3 Dec",
      legNm: 1350,
    },
    {
      name: "Rodney Bay, St Lucia",
      lat: "14°04′N",
      lng: "60°57′W",
      type: "ARRIVAL",
      etaLabel: "13 Dec",
      notes: "ARC finish line",
      legNm: 2700,
    },
  ] as WaypointEntry[],

  // TODO(backend/tracking): replace with VesselPings WebSocket subscription
  tracking: {
    position: "23° 14′ N, 38° 55′ W",
    speed: "7.4 kn",
    heading: "262° W",
    wind: "NE 18 kn",
    lastPing: "4 min ago",
    vessel: "SY Aventura II",
  } as LiveTracking,

  // TODO(backend): crew comes from ExpeditionBySlug crew[] — missing initials/paid/joinType display
  crew: [
    {
      id: "1",
      initials: "SC",
      name: "Sofia Cardoso",
      email: "sofia@example.com",
      role: "SKIPPER",
      status: "CONFIRMED",
      joinType: "invited",
      milesEarned: 1020,
      paid: true,
      isOwner: true,
      vesselId: "v1",
    },
    {
      id: "2",
      initials: "NF",
      name: "Nuno Ferreira",
      email: "nuno@example.com",
      role: "FIRST_MATE",
      status: "CONFIRMED",
      joinType: "invited",
      milesEarned: 1020,
      paid: true,
      vesselId: "v1",
    },
    {
      id: "3",
      initials: "AM",
      name: "Ana Matos",
      email: "ana@example.com",
      role: "CREW",
      status: "CONFIRMED",
      joinType: "paid_berth",
      milesEarned: 1020,
      paid: true,
      vesselId: "v1",
    },
    {
      id: "4",
      initials: "CR",
      name: "Carlos Rodrigues",
      email: "carlos@example.com",
      role: "SKIPPER",
      status: "CONFIRMED",
      joinType: "invited",
      milesEarned: 1020,
      paid: true,
      vesselId: "v2",
    },
    {
      id: "5",
      initials: "BS",
      name: "Beatriz Santos",
      email: "beatriz@example.com",
      role: "STUDENT",
      status: "CONFIRMED",
      joinType: "paid_berth",
      milesEarned: 1020,
      paid: false,
      vesselId: "v2",
    },
    {
      id: "6",
      initials: "TK",
      name: "Tiago Kühn",
      email: "tiago@example.com",
      role: "OBSERVER",
      status: "CONFIRMED",
      joinType: "paid_berth",
      milesEarned: undefined,
      paid: true,
      vesselId: "v1",
    },
  ] as CrewMember[],

  // TODO(backend/documents): post-MVP — add document storage to schema
  documents: [
    { id: "1", name: "Crew List (ARC Official)", type: "Crew List", uploadedLabel: "18 Nov 2026" },
    { id: "2", name: "Safety Equipment Certificate", type: "Certificate", uploadedLabel: "10 Nov 2026" },
    { id: "3", name: "Passage Plan", type: "Route Plan", uploadedLabel: "15 Nov 2026" },
  ] as DocumentEntry[],

  // TODO(backend): expenses come from ExpeditionExpensesList — missing category/paidBy display
  expenses: [
    {
      id: "e1",
      description: "Marina fees — Las Palmas",
      amount: 320,
      displayAmount: "€320",
      category: "Marina fees",
      paidBy: "Sofia Cardoso",
      settled: true,
    },
    {
      id: "e2",
      description: "Fuel top-up · 260 L",
      amount: 390,
      displayAmount: "€390",
      category: "Fuel",
      paidBy: "Sofia Cardoso",
      settled: false,
    },
    {
      id: "e3",
      description: "Provisioning — dry goods",
      amount: 640,
      displayAmount: "€640",
      category: "Provisioning",
      paidBy: "Nuno Ferreira",
      settled: false,
    },
    {
      id: "e4",
      description: "ARC rally entry fees",
      amount: 1110,
      displayAmount: "€1,110",
      category: "Entry fees",
      paidBy: "Sofia Cardoso",
      settled: true,
    },
    {
      id: "e5",
      description: "Safety equipment — flares",
      amount: 180,
      displayAmount: "€180",
      category: "Safety",
      paidBy: "Sofia Cardoso",
      settled: true,
    },
  ] as ExpenseEntry[],

  // TODO(backend/activity): post-MVP — add activity log to schema
  activity: [
    {
      id: "a1",
      title: "Vessel position updated",
      detail: "23°14′N 38°55′W · 7.4 kn · NE trade wind",
      timeLabel: "4 min ago",
      color: "success",
    },
    {
      id: "a2",
      title: "Expense logged — Fuel top-up",
      detail: "€390 · Added by Sofia Cardoso",
      timeLabel: "2 h ago",
      color: "warning",
    },
    {
      id: "a3",
      title: "Halfway marker passed",
      detail: "1,350 nm completed of 2,700 nm total route",
      timeLabel: "Yesterday",
      color: "primary",
    },
    {
      id: "a4",
      title: "Cape Verde stop skipped",
      detail: "Skipper decision — conditions favourable for direct passage",
      timeLabel: "26 Nov",
      color: "neutral",
    },
    {
      id: "a5",
      title: "Expedition activated — voyage underway",
      detail: "State advanced from OPEN → ACTIVE by Sofia Cardoso",
      timeLabel: "22 Nov",
      color: "success",
    },
  ] as ActivityEntry[],
};
